import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Underline from '@tiptap/extension-underline';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Link from '@tiptap/extension-link';
import { Item, useCanvasStore } from '@/stores/canvasStore';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Download, Copy, MoreHorizontal } from 'lucide-react';
import { toast } from 'sonner';
import { InlineAIButton, InlineAIPanelPortal } from '@/components/ai';
import { cn } from '@/lib/utils';

interface TextCardProps {
  item: Item;
  onHeightChange?: (newHeight: number) => void;
}

export function TextCard({ item, onHeightChange }: TextCardProps) {
  const content = item.content as { title?: string; body?: string; partnr_generated?: boolean; transparent?: boolean };
  const [isHovering, setIsHovering] = useState(false);
  const [hoverZone, setHoverZone] = useState<'drag' | 'content' | null>(null);
  const [selectedText, setSelectedText] = useState('');
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const lastHeightRef = useRef<number>(item.height);
  const heightCheckTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const { updateItem, newlyCreatedItemId, clearNewlyCreatedItem, setIsEditingItem, setActiveEditor, setItemMinHeight } = useCanvasStore();
  
  const isPartnrGenerated = !!content.partnr_generated;
  const isTransparent = !!content.transparent;

  // Get initial content - handle backwards compatibility
  const initialContent = content.body || content.title || '';

  // Convert markdown-ish text to HTML for TipTap
  const textToHtml = (text: string): string => {
    if (!text) return '<p></p>';
    // If it looks like HTML already, return as-is
    if (text.startsWith('<')) return text;
    // Convert plain text with newlines to paragraphs
    return text.split('\n\n').map(p => `<p>${p.replace(/\n/g, '<br>')}</p>`).join('');
  };

  // Store onHeightChange in a ref to avoid dependency issues
  const onHeightChangeRef = useRef(onHeightChange);
  onHeightChangeRef.current = onHeightChange;

  // Calculate and update height based on content — GROW ONLY, never auto-shrinks.
  // Auto-shrinking causes feedback loops and fighting with manual resize.
  // Users can manually resize cards smaller via drag handles.
  const calculateAndUpdateHeight = useCallback(() => {
    if (!contentRef.current) return;

    const contentHeight = contentRef.current.scrollHeight;
    const buffer = 4;
    const minHeight = 80;
    // Cap auto-height at 80% of viewport so tall cards scroll internally
    const maxAutoHeight = typeof window !== 'undefined' ? window.innerHeight * 0.8 : 800;

    const fullRequiredHeight = Math.max(minHeight, contentHeight + buffer);
    const cappedHeight = Math.min(maxAutoHeight, fullRequiredHeight);

    // Set a small fixed min height — NOT the content height.
    // This lets users freely resize cards smaller (content scrolls internally).
    setItemMinHeight(item.id, minHeight);

    // GROW ONLY: only call onHeightChange if content needs MORE space than the card currently has.
    // This prevents hover/reflow loops from shrinking or re-growing the card.
    if (cappedHeight > lastHeightRef.current + 5) {
      lastHeightRef.current = cappedHeight;
      if (onHeightChangeRef.current) {
        onHeightChangeRef.current(cappedHeight);
      }
    }
  }, [item.id, setItemMinHeight]);

  // Debounced height check for typing
  const scheduleHeightCheck = useCallback(() => {
    if (heightCheckTimeoutRef.current) {
      clearTimeout(heightCheckTimeoutRef.current);
    }
    heightCheckTimeoutRef.current = setTimeout(calculateAndUpdateHeight, 100);
  }, [calculateAndUpdateHeight]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        bulletList: {
          keepMarks: true,
          keepAttributes: false,
        },
        orderedList: {
          keepMarks: true,
          keepAttributes: false,
        },
      }),
      Underline,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-primary underline cursor-pointer',
        },
      }),
      Placeholder.configure({
        placeholder: 'Start typing...',
      }),
    ],
    content: textToHtml(initialContent),
    editorProps: {
      attributes: {
        class: 'outline-none min-h-[1.5em] text-sm text-foreground prose prose-sm dark:prose-invert max-w-none prose-ul:list-disc prose-ul:pl-4 prose-ol:list-decimal prose-ol:pl-4 prose-li:my-0.5 [&_ul[data-type="taskList"]]:list-none [&_ul[data-type="taskList"]]:pl-0',
      },
      handleKeyDown: (view, event) => {
        // Handle Tab for list indentation
        if (event.key === 'Tab') {
          const { state, dispatch } = view;
          const { selection } = state;
          
          // Check if we're in a list
          const { $from } = selection;
          const listItem = $from.node(-1);
          
          if (listItem?.type.name === 'listItem') {
            event.preventDefault();
            if (event.shiftKey) {
              // Outdent: lift list item
              return editor?.chain().liftListItem('listItem').run() ?? false;
            } else {
              // Indent: sink list item
              return editor?.chain().sinkListItem('listItem').run() ?? false;
            }
          }
        }
        return false;
      },
    },
    onUpdate: ({ editor }) => {
      // Save content as HTML
      const html = editor.getHTML();
      updateItem(item.id, {
        content: { body: html },
      });
      // Schedule height check after content update
      scheduleHeightCheck();
    },
    onFocus: () => {
      setIsFocused(true);
      setIsEditingItem(true);
      if (editor) {
        setActiveEditor(editor);
      }
    },
    onBlur: () => {
      setIsFocused(false);
      setIsEditingItem(false);
      setActiveEditor(null);
    },
    onSelectionUpdate: ({ editor }) => {
      const selection = editor.state.selection;
      if (!selection.empty) {
        const text = editor.state.doc.textBetween(selection.from, selection.to);
        setSelectedText(text);
      } else {
        setSelectedText('');
      }
    },
  });

  // Auto-focus when newly created
  useEffect(() => {
    if (newlyCreatedItemId === item.id && editor) {
      editor.commands.focus('end');
      clearNewlyCreatedItem();
    }
  }, [newlyCreatedItemId, item.id, clearNewlyCreatedItem, editor]);

  // Calculate height after editor is ready and on initial render
  useEffect(() => {
    if (editor && contentRef.current) {
      // Small delay to ensure DOM is fully rendered
      const timeout = setTimeout(calculateAndUpdateHeight, 50);
      return () => clearTimeout(timeout);
    }
  }, [editor, calculateAndUpdateHeight]);

  // Note: No ResizeObserver or initialContent watcher for auto-height.
  // Auto-height ONLY runs from typing (editor onUpdate → scheduleHeightCheck)
  // and once on mount. This prevents hover/reflow/resize loops.
  // If the card is resized smaller than content, content scrolls internally.

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (heightCheckTimeoutRef.current) {
        clearTimeout(heightCheckTimeoutRef.current);
      }
    };
  }, []);

  const getPlainText = (): string => {
    return editor?.getText() || '';
  };

  const handleCopyText = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(getPlainText());
    toast.success('Copied to clipboard');
  };

  const handleDownloadMarkdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    const text = getPlainText();
    const blob = new Blob([text], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `text-card.md`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('Downloaded');
  };

  // Replace selected text with AI result
  const handleReplaceText = (newText: string) => {
    if (editor && selectedText) {
      editor.chain().focus().insertContent(newText).run();
      setSelectedText('');
      // Recalculate height after AI content insertion
      setTimeout(calculateAndUpdateHeight, 50);
      toast.success('Text replaced');
    }
  };

  // Insert AI result at end
  const handleInsertText = (newText: string) => {
    if (editor) {
      editor.chain().focus('end').insertContent(`<p>${newText}</p>`).run();
      // Recalculate height after AI content insertion
      setTimeout(calculateAndUpdateHeight, 50);
      toast.success('Text inserted');
    }
  };

  // Determine which zone the mouse is in (top/bottom 20% = drag, middle 60% = content)
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!containerRef.current || isFocused) return;
    
    const rect = containerRef.current.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const heightPercent = y / rect.height;
    
    if (heightPercent < 0.15 || heightPercent > 0.85) {
      setHoverZone('drag');
    } else {
      setHoverZone('content');
    }
  };

  const handleClick = (e: React.MouseEvent) => {
    if (hoverZone === 'content' && editor && !isFocused) {
      e.stopPropagation();
      editor.commands.focus();
    }
  };

  // Get cursor based on zone
  const getCursor = () => {
    if (isFocused) return 'text';
    if (hoverZone === 'drag') return 'grab';
    if (hoverZone === 'content') return 'text';
    return 'default';
  };

  return (
    <div 
      ref={containerRef}
      className={cn(
        "w-full h-full rounded-lg flex flex-col group relative transition-all duration-200",
        isTransparent
          ? "bg-transparent"
          : "bg-card card-shadow",
        isPartnrGenerated
          ? "border-2 border-dashed border-partnr/30"
          : isTransparent
            ? isFocused ? "border border-primary/30 shadow-sm" : isHovering ? "border border-border/40" : "border border-transparent"
            : isFocused ? "border border-primary/30 shadow-md" : "border border-border"
      )}
      style={{ cursor: getCursor() }}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => { setIsHovering(false); setHoverZone(null); }}
      onMouseMove={handleMouseMove}
      onClick={handleClick}
      data-drag-zone={hoverZone === 'drag' ? 'true' : 'false'}
    >
      {/* Export menu - visible on hover */}
      <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon" 
              className="h-7 w-7 bg-background/80 backdrop-blur-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover">
            <DropdownMenuItem onClick={handleCopyText}>
              <Copy className="mr-2 h-4 w-4" />
              Copy text
            </DropdownMenuItem>
            <DropdownMenuItem onClick={handleDownloadMarkdown}>
              <Download className="mr-2 h-4 w-4" />
              Download as .md
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Content area - scrollable when content exceeds card height */}
      <div
        ref={contentRef}
        className="flex-1 p-4 pr-2 overflow-y-auto overflow-x-hidden min-h-0 scrollbar-on-hover"
        onMouseDown={(e) => {
          if (isFocused) e.stopPropagation();
        }}
        onKeyDown={(e) => e.stopPropagation()}
        onContextMenu={(e) => {
          // Let the browser show its native context menu (spellcheck,
          // autocorrect suggestions, copy/paste, etc.) instead of the
          // CanvasItem's Radix ContextMenu. The card-level menu is still
          // reachable via the edge drag zones or the hover "more" button.
          e.stopPropagation();
        }}
        onWheel={(e) => {
          // When the content is scrollable, prevent canvas zoom/pan from intercepting.
          // Works on hover — users shouldn't need to click into the editor first.
          if (contentRef.current) {
            const el = contentRef.current;
            const hasScroll = el.scrollHeight > el.clientHeight;
            if (hasScroll) {
              e.stopPropagation();
            }
          }
        }}
      >
        <EditorContent editor={editor} className="w-full h-full" />
      </div>

      {/* AI Assistant Button */}
      <InlineAIButton
        onClick={() => setShowAIPanel(!showAIPanel)}
        isActive={showAIPanel}
        className="opacity-0 group-hover:opacity-100 transition-opacity"
      />

      {/* AI Panel - rendered via portal next to the card */}
      {showAIPanel && (
        <InlineAIPanelPortal
          cardRef={containerRef}
          selectedText={selectedText}
          fullContent={getPlainText()}
          onReplace={handleReplaceText}
          onInsert={handleInsertText}
          onClose={() => setShowAIPanel(false)}
        />
      )}
    </div>
  );
}
